import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
    IAddItemBase,
    ICartItemData,
    IBookingCart,
    IUpdateCartItemData,
    IActiveDiscountDetail,
    ICustomerDetail,
    IOptionalField,
} from "@interfaces";
import { CartConstants } from "@constants";
import { cartApi } from "@api";
import { InitStages, LoadingStage } from "src/Interfaces/shared";
import {
    getItemsPriceInfo,
    updateItems,
    buildOptionalFieldsPayload,
    buildItemsForNewQuantity,
    transformCartItemsToUpdateCartItemsData,
} from "./checkoutHelpers";
import { planCartAddition } from "./cartAdditionPlan";
import { isDuplicateCartItemError } from "./cartErrors";
import { RemovePayloadCart } from "@utilities";
import { discountApi } from "src/Api/discountApi";
import { isNil } from "lodash";
import { store } from "@redux";

// Function to update totalPrice
const setTotalPrice = (state: IBookingCart) => {
    const priceInfo = getItemsPriceInfo(state.cartItems);
    state.totalRRP = priceInfo.totalRRP;
    state.totalNet = priceInfo.totalNetRate;
    state.commission = priceInfo.commission;
    state.commissionPercent = priceInfo.commissionPercent;
    state.totalPrice = priceInfo.totalPrice;
};

const initialState: IBookingCart = {
    cartItems: [],
    cartItemsLoadingStage: LoadingStage.NOT_INITIALIZED,
    totalRRP: 0,
    totalNet: 0,
    totalPrice: 0,
    commission: 0,
    commissionPercent: 0,
    customers: [],
    customersLoadingStage: LoadingStage.NOT_INITIALIZED,
    isShare: false,
    optionalFieldValidated: true,
    canCheckout: false,
    redirectUrl: "",
    bookingReference: "",
    step: CartConstants.CART,
    currentStep: CartConstants.CART,
};

export const INCREASE_QTY = "increase";
export const DECREASE_QTY = "decrease";

export const fetchActiveDiscount = createAsyncThunk<IActiveDiscountDetail>(
    "booking/fetchActiveDiscount",
    async (_, { rejectWithValue }) => {
        try {
            const state = store.getState();
            const metadata = state.account.metadata;
            if (metadata?.is_points_agent || metadata?.is_commission_agent) {
                return rejectWithValue("Integrated agents do not use PTX discounts.");
            }
            const isAuthenticated = !isNil(state.auth.jwt) && !isNil(state.account.account);
            const response = await discountApi.getActiveDiscount(isAuthenticated);
            return response.data.data;
        } catch (error) {
            console.error("Failed to get active discount:", error);
            return rejectWithValue("Unable to load active discount.");
        }
    },
);

export const fetchCartItems = createAsyncThunk<
    ICartItemData[],
    string | undefined,
    { rejectValue: string }
>("booking/fetchCartItems", async (sessionId, { rejectWithValue }) => {
    try {
        // Don't fetch cart items if user has temp JWT and no sessionId (during signup/verification flow)
        const tempJwt = sessionStorage.getItem("tempJwt");
        if (tempJwt && !sessionId) {
            return [];
        }

        const fetchCart = async (): Promise<ICartItemData[]> => {
            const res = await cartApi.getCart(sessionId);
            return res.data.data;
        };

        const items = await fetchCart();
        const allLatest =
            items.length > 0 && items.every((it: ICartItemData) => !!it.is_product_latest);
        if (!allLatest && items.length > 0) {
            if (sessionId) {
                // For session-based carts, we can't use updateCartItemsForCart (it's for authenticated users only)
                // Just return the items as-is since session carts are temporary
                return items;
            } else {
                // For authenticated users without sessionId, update cart items
                await cartApi.updateCartItemsForCart();
                return await fetchCart();
            }
        }
        return items;
    } catch (err: any) {
        console.error("Failed to fetch the cart:", err);
        return rejectWithValue("Unable to fetch cart data.");
    }
});

/**
 * Add tickets to the cart, raising the quantity of any already there.
 *
 * `POST /cart/items/v2` refuses a booking the cart already holds — "Item already
 * exists" — rather than adding to it, so pressing Add to Cart or Book Now again
 * for the same fare used to fail outright. The post is still tried first, since
 * it is the only call needed in the common case; only a duplicate rejection
 * sends us down the slower path of reading the cart, raising the quantity on the
 * lines that clash, and posting whatever is left.
 *
 * Lives here rather than in its own module on purpose: checkoutHelpers imports
 * the `@api` barrel, so a separate file importing both that and `src/Api/cartApi`
 * re-entered the barrel mid-evaluation and died on `attachJwtAuthInterceptors`
 * before initialization. This file already imports both.
 */
const addOrAccumulate = async (
    item: IAddItemBase,
    sessionId?: string,
    knownRows: ICartItemData[] = [],
): Promise<void> => {
    // Planned from the rows already in the store when they cover the clash, so
    // the doomed post is never made. It would be handled either way, but it
    // leaves a red 400 in the console that reads like a fault.
    let rows = knownRows;
    let planned = planCartAddition(item, rows);

    if (planned.updates.length === 0) {
        try {
            await cartApi.addCart(item, sessionId);
            return;
        } catch (error) {
            if (!isDuplicateCartItemError(error)) throw error;
        }

        // The store was stale — it did not know about the clashing line — so read
        // the cart for the truth and plan again.
        const existing = await cartApi.getCart(sessionId);
        rows = (existing.data.data ?? []) as ICartItemData[];
        planned = planCartAddition(item, rows);
    }

    const { updates, remainder } = planned;

    if (updates.length > 0) {
        // Rebuilt rather than patched: raising the count has to grow booking_data
        // to match, or the extra travellers have no rows.
        const updated = updates.flatMap(({ row, newQuantity }) =>
            buildItemsForNewQuantity([row], newQuantity, [row]),
        );
        await cartApi.updateCartItems(transformCartItemsToUpdateCartItemsData(updated), sessionId);
    }

    if (remainder) {
        await cartApi.addCart(remainder, sessionId);
    }

    // Nothing matched and nothing was left to post: the clash is on something
    // this plan cannot see, so the original refusal stands.
    if (updates.length === 0 && !remainder) {
        throw new Error("Item already exists");
    }
};

export const addToCartAndFetch = createAsyncThunk<
    ICartItemData[],
    { item: IAddItemBase; sessionId?: string }
>("booking/addToCartAndFetch", async ({ item, sessionId }, { rejectWithValue, getState }) => {
    try {
        const state = getState() as { checkout?: { cartItems?: ICartItemData[] } };
        await addOrAccumulate(item, sessionId, state.checkout?.cartItems ?? []);
        const response = await cartApi.getCart(sessionId);
        return response.data.data as ICartItemData[];
    } catch (error: any) {
        console.error("Failed to add item to cart or fetch updated cart:", error);
        const errorMessage = error?.response?.data?.title || "Unable to add item to cart";
        return rejectWithValue(errorMessage);
    }
});

export const submitCartDataAndFetch = createAsyncThunk<
    any,
    {
        items: IUpdateCartItemData[];
        sessionId?: string;
        customers?: ICustomerDetail[];
        optionalFields?: IOptionalField[];
    }
>(
    "booking/submitCartDataAndFetch",
    async ({ items, sessionId, customers, optionalFields }, { rejectWithValue }) => {
        try {
            const responseSubmit = await updateItems(items, "cart", sessionId);
            if (responseSubmit.success === true && responseSubmit.code === 200) {
                // Update customer/guest MongoDB collections with optional_fields
                if (customers && optionalFields && items.length > 0) {
                    // Get all unique redeemer IDs from booking_data
                    const redeemerIds = new Set<number>();
                    const redeemerOptionalData = new Map<number, Record<string, any>>();

                    for (const item of items) {
                        for (const bd of item.bookingData || []) {
                            if (bd.redeemers) {
                                for (const redeemerId of bd.redeemers) {
                                    redeemerIds.add(redeemerId);
                                    // Store the optionalData for this redeemer (last one wins if multiple)
                                    if (bd.optionalData) {
                                        redeemerOptionalData.set(redeemerId, bd.optionalData);
                                    }
                                }
                            }
                        }
                    }

                    // Update each customer/guest that is a redeemer
                    const updatePromises: Promise<any>[] = [];
                    for (const redeemerId of Array.from(redeemerIds)) {
                        const customer = customers.find((c) => Number(c.id) === redeemerId);
                        if (!customer) continue;

                        const optionalData = redeemerOptionalData.get(redeemerId);
                        const optionalFieldsPayload = buildOptionalFieldsPayload(
                            optionalData,
                            optionalFields,
                        );

                        if (customer.is_primary && customer.customer) {
                            // Update customer collection
                            const payload = {
                                ...customer.customer,
                                optional_fields: optionalFieldsPayload,
                            };
                            updatePromises.push(
                                cartApi.updateCustomer(payload, sessionId).catch((err) => {
                                    console.error("Failed to update customer:", err);
                                }),
                            );
                        } else if (!customer.is_primary && customer.guest) {
                            // Update guest collection
                            const payload = {
                                ...customer.guest,
                                optional_fields: optionalFieldsPayload,
                            };
                            updatePromises.push(
                                cartApi.updateGuest(payload, sessionId).catch((err) => {
                                    console.error("Failed to update guest:", err);
                                }),
                            );
                        }
                    }

                    // Wait for all updates to complete (don't fail if some fail)
                    await Promise.all(updatePromises);
                }

                const response = await cartApi.getCart(sessionId);
                return response.data.data as ICartItemData[];
            } else {
                throw new Error("Cart update failed");
            }
        } catch (error: any) {
            return rejectWithValue(error.response?.data || error.message);
        }
    },
);
export const removeCartItemAndFetch = createAsyncThunk<
    ICartItemData[],
    RemovePayloadCart & { sessionId?: string }
>("booking/removeCartAndFetch", async ({ sessionId, ...payload }, { rejectWithValue }) => {
    try {
        if (payload.type === "item") {
            await cartApi.removeCartItemById(payload.id, sessionId);
        } else {
            await cartApi.removeCartItem(payload, sessionId);
        }
        const response = await cartApi.getCart(sessionId);
        return response.data.data as ICartItemData[];
    } catch (error) {
        console.error("Failed to remove/fetch cart:", error);
        return rejectWithValue("Unable to update cart after removal.");
    }
});

const checkout = createSlice({
    name: "bookingCart",
    initialState: initialState,
    reducers: {
        resetCheckout: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCartItems.pending, (state) => {
                state.cartItemsLoadingStage = InitStages.includes(state.cartItemsLoadingStage)
                    ? LoadingStage.IS_INITIALIZING
                    : LoadingStage.IS_LOADING;
            })
            .addCase(fetchCartItems.fulfilled, (state, action) => {
                try {
                    state.cartItems = action.payload;
                    setTotalPrice(state);
                    state.cartItemsLoadingStage = InitStages.includes(state.cartItemsLoadingStage)
                        ? LoadingStage.INITIALIZE_COMPLETE
                        : LoadingStage.LOADING_COMPLETE;
                    setTotalPrice(state);
                } catch (error) {
                    console.error("Failed to update cart:", error);
                    state.cartItemsLoadingStage = InitStages.includes(state.cartItemsLoadingStage)
                        ? LoadingStage.INITIALIZE_FAILURE
                        : LoadingStage.LOADING_FAILURE;
                }
            })
            .addCase(fetchCartItems.rejected, (state, action) => {
                console.error("Failed to fetch cart:", action.payload);
                state.cartItemsLoadingStage = InitStages.includes(state.cartItemsLoadingStage)
                    ? LoadingStage.INITIALIZE_FAILURE
                    : LoadingStage.LOADING_FAILURE;
            })
            .addCase(addToCartAndFetch.fulfilled, (state, action) => {
                try {
                    state.cartItems = action.payload;
                    setTotalPrice(state);
                    state.cartItemsLoadingStage = LoadingStage.LOADING_COMPLETE;
                } catch (error) {
                    console.error("Failed to update cart:", error);
                    state.cartItemsLoadingStage = LoadingStage.LOADING_FAILURE;
                }
            })
            .addCase(addToCartAndFetch.pending, (state) => {
                state.cartItemsLoadingStage = LoadingStage.IS_LOADING;
            })
            .addCase(addToCartAndFetch.rejected, (state, action) => {
                console.error("Failed to update cart:", action.payload);
                state.cartItemsLoadingStage = LoadingStage.LOADING_FAILURE;
            })
            .addCase(submitCartDataAndFetch.rejected, (state, action) => {
                console.error("Failed to submit cart data and fetch", action.payload);
                state.cartItemsLoadingStage = LoadingStage.LOADING_FAILURE;
            })
            .addCase(submitCartDataAndFetch.fulfilled, (state, action) => {
                try {
                    state.cartItems = action.payload;
                    setTotalPrice(state);
                    state.cartItemsLoadingStage = LoadingStage.LOADING_COMPLETE;
                } catch (error) {
                    console.error("Failed to update cart:", error);
                    state.cartItemsLoadingStage = LoadingStage.LOADING_FAILURE;
                }
            })
            .addCase(removeCartItemAndFetch.fulfilled, (state, action) => {
                state.cartItems = action.payload;
                setTotalPrice(state);
                state.cartItemsLoadingStage = LoadingStage.LOADING_COMPLETE;
            })
            .addCase(removeCartItemAndFetch.pending, (state) => {
                state.cartItemsLoadingStage = LoadingStage.IS_LOADING;
            })
            .addCase(removeCartItemAndFetch.rejected, (state, action) => {
                console.error("Failed to remove item from cart:", action.payload);
                state.cartItemsLoadingStage = LoadingStage.LOADING_FAILURE;
            })
            .addCase(fetchActiveDiscount.fulfilled, (state, action) => {
                state.activeDiscount = action.payload;
            })
            .addCase(fetchActiveDiscount.rejected, () => {
                return;
            });
    },
});

export const { resetCheckout } = checkout.actions;
export const checkoutReducer = checkout.reducer;
